/**
 * PudoSelector - Reusable PUDO location selector with parish filter and search
 * Used in SendPackage and Checkout pages
 */
import React, { useState, useMemo } from 'react';
import { Building2, Search, MapPin, CheckCircle, Clock, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const JAMAICA_PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

export function PudoSelector({
  pudos = [],
  selectedPudoId,
  onSelect,
  loading = false,
  label = "Select PUDO Location",
  placeholder = "Select a PUDO centre",
  darkMode = false,
  testIdPrefix = "pudo"
}) {
  const [parishFilter, setParishFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter PUDOs by parish and search term
  const filteredPudos = useMemo(() => {
    return pudos.filter(pudo => {
      // Parish filter
      const pudoParish = pudo.address?.parish || pudo.parish || '';
      const matchesParish = !parishFilter || 
        pudoParish.toLowerCase().includes(parishFilter.toLowerCase());
      
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (pudo.name && pudo.name.toLowerCase().includes(searchLower)) ||
        (pudo.address?.street && pudo.address.street.toLowerCase().includes(searchLower)) ||
        (pudo.address?.city && pudo.address.city.toLowerCase().includes(searchLower)) ||
        (pudoParish && pudoParish.toLowerCase().includes(searchLower));
      
      return matchesParish && matchesSearch;
    });
  }, [pudos, parishFilter, searchTerm]);

  // Find selected PUDO details
  const selectedPudo = useMemo(() => {
    return pudos.find(p => p.id === selectedPudoId);
  }, [pudos, selectedPudoId]);

  const baseInputClass = darkMode 
    ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400"
    : "bg-white border-gray-200 text-gray-900 placeholder-gray-500";
  
  const baseLabelClass = darkMode ? "text-gray-300" : "text-gray-700";

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        Loading PUDO locations...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Parish Filter */}
      <div>
        <Label className={`${baseLabelClass} font-medium mb-2 block`}>
          <MapPin className="w-4 h-4 inline mr-1" />
          Filter by Parish
        </Label>
        <Select 
          value={parishFilter} 
          onValueChange={(val) => {
            setParishFilter(val === 'all' ? '' : val);
            // Reset selected PUDO when parish changes
            if (selectedPudoId && val !== 'all') {
              const currentPudo = pudos.find(p => p.id === selectedPudoId);
              const pudoParish = currentPudo?.address?.parish || currentPudo?.parish || '';
              if (!pudoParish.toLowerCase().includes(val.toLowerCase())) {
                onSelect(null);
              }
            }
          }}
        >
          <SelectTrigger 
            className={`${baseInputClass} h-11 rounded-lg`}
            data-testid={`${testIdPrefix}-parish-select`}
          >
            <SelectValue placeholder="All Parishes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Parishes</SelectItem>
            {JAMAICA_PARISHES.map(parish => (
              <SelectItem key={parish} value={parish}>{parish}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search Input */}
      <div>
        <Label className={`${baseLabelClass} font-medium mb-2 block`}>
          <Search className="w-4 h-4 inline mr-1" />
          Search PUDO Centres
        </Label>
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <Input 
            type="text"
            placeholder="Search by name, address, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${baseInputClass} pl-10 h-11 rounded-lg`}
            data-testid={`${testIdPrefix}-search-input`}
          />
        </div>
      </div>

      {/* PUDO Selection Dropdown */}
      <div>
        <Label className={`${baseLabelClass} font-medium mb-2 block`}>
          <Building2 className="w-4 h-4 inline mr-1" />
          {label}
        </Label>
        
        {filteredPudos.length === 0 ? (
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-800/50 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'} text-center`}>
            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No PUDO locations found</p>
            {parishFilter && <p className="text-sm">Try selecting a different parish</p>}
          </div>
        ) : (
          <Select value={selectedPudoId || ''} onValueChange={onSelect}>
            <SelectTrigger 
              className={`${baseInputClass} h-12 rounded-lg`}
              data-testid={`${testIdPrefix}-location-select`}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {filteredPudos.map(pudo => (
                <SelectItem key={pudo.id} value={pudo.id}>
                  <div className="flex items-center gap-3 py-1">
                    <Building2 className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="font-medium">{pudo.name}</p>
                      <p className="text-xs text-gray-500">
                        {pudo.address?.street || pudo.address || ''}{pudo.address?.city ? `, ${pudo.address.city}` : ''}
                      </p>
                      <p className="text-xs text-gray-400">
                        {pudo.address?.parish || pudo.parish || ''}
                      </p>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Selected PUDO Details Card */}
        {selectedPudo && (
          <div className={`mt-3 p-4 rounded-lg border ${darkMode ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${darkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                <Building2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedPudo.name}
                  </p>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedPudo.address?.street || selectedPudo.address || ''}
                </p>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {selectedPudo.address?.city || ''}, {selectedPudo.address?.parish || selectedPudo.parish || ''}
                </p>
                {selectedPudo.operating_hours && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    <Clock className="w-3 h-3" />
                    {selectedPudo.operating_hours}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results Count */}
        <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {filteredPudos.length} of {pudos.length} PUDO locations shown
        </p>
      </div>
    </div>
  );
}

export default PudoSelector;
